import React from 'react';
import MultiSelectAutocomplete from '@/components/FormFields/MultiSelectAutocomplete';
import NumberTextField from '@/components/FormFields/NumberTextField';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import { Button, FieldError } from '@/ui';
import { Field } from 'redux-form';
import NativeSelectField from '../FormFields/NativeSelectField';
import { renderComboBox, renderToggleSwitch, renderTrueOrFalse } from '../FormFields/reduxFormRenderers';

/** These rows label a control to its left, the way every pane in this tree is laid out. */
const ROW_LABEL_SX = { display: 'block', mt: 1 };

export const ToggleSwitchRow = ({ label, name }) => (
  <Grid container spacing={2} sx={{ alignItems: 'center' }}>
    <Grid size={{ xs: 12, sm: 6 }}>
      <FormLabel component='span'>{label}</FormLabel>
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <Field name={name} component={renderToggleSwitch} />
    </Grid>
  </Grid>
);

export function MultiselectField({ input, meta, data, textField, busy = false }) {
  return (
    <>
      <MultiSelectAutocomplete
        name={input.name}
        options={data}
        value={input.value}
        onChange={input.onChange}
        onBlur={() => input.onBlur(input.value)}
        placeholder='Select'
        textField={textField || 'name'}
        dataKey='id'
        busy={busy}
        error={Boolean(meta.touched && meta.error)}
      />
      <FieldError error={meta.error} touched={meta.touched} />
    </>
  );
}

export const SelectField = ({ input, meta, options }) => (
  <div>
    <NativeSelectField {...input} error={Boolean(meta.touched && meta.error)}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.option}
        </option>
      ))}
    </NativeSelectField>
    <FieldError error={meta.error} touched={meta.touched} />
  </div>
);

export function SelectRow({
  name,
  label,
  options = [
    { value: '', option: 'None' },
    { value: 'Include', option: 'Include' },
    { value: 'Exclude', option: 'Exclude' },
  ],
  children,
}) {
  return (
    <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
      <Grid size={{ xs: 12, lg: 2 }}>
        <Typography variant='h5'>{label}</Typography>
      </Grid>
      <Grid size={{ xs: 3, lg: 2 }}>
        <Field name={name} component={SelectField} options={options} />
      </Grid>
      {children}
    </Grid>
  );
}

export const Condition = ({ when, is, children }) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (is.includes(value) ? children : null)}
  </Field>
);

export const ColCondition = ({ when, children }) => (
  <Grid size='grow'>
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => (value ? children : null)}
    </Field>
  </Grid>
);

//TODO: Had to remove the format because it wasn't working with the redux-form Field component

export const NumberField = ({ input, meta, label, min = 0 }) => {
  return (
    <>
      <FormLabel component='span' sx={ROW_LABEL_SX}>
        {label}
      </FormLabel>
      <NumberTextField
        name={input.name}
        min={min}
        placeholder={label}
        value={input.value}
        onChange={(value) => input.onChange(value)}
        error={Boolean(meta.touched && meta.error)}
      />
      <FieldError error={meta.error} touched={meta.touched} />
    </>
  );
};

export const PercentageField = ({ input, meta, label, allowNegative = false, inlineLabel = false }) => {
  const value = typeof input.value === 'string' ? parseFloat(input.value) || 0 : input.value;

  return (
    <Box sx={{ display: inlineLabel ? 'flex' : 'block', alignItems: 'center', gap: 2 }}>
      <FormLabel component='span' sx={{ display: 'block', m: inlineLabel ? 0 : undefined }}>
        {label}
      </FormLabel>
      <Box sx={{ flex: 1 }}>
        <NumberTextField
          percent
          name={input.name}
          placeholder={label}
          min={allowNegative ? -1 : 0}
          max={1}
          step={0.01}
          value={value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          error={Boolean(meta.touched && meta.error)}
          sx={{ maxWidth: '15em' }}
        />
        <FieldError error={meta.error} touched={meta.touched} />
      </Box>
    </Box>
  );
};

const normalizeBoolean = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

export const UserAttributes = ({ fields, data }) => (
  <div>
    <Button color='success' sx={{ float: 'right' }} onClick={() => fields.push({ user_attribute_value: true })}>
      Add User Attribute
    </Button>
    <Grid size={{ xs: 12, lg: 9 }}>
      {fields.map((criteriaUserAttributes, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <Field
                name={`${criteriaUserAttributes}.user_attribute`}
                component={renderComboBox}
                textField='name'
                data={data?.map((item) => item?.name)}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Field
                name={`${criteriaUserAttributes}.user_attribute_value`}
                component={renderTrueOrFalse}
                normalize={normalizeBoolean}
                type='select'
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }}>
              <Button color='danger' type='button' title='Remove Parent Attribute' onClick={() => fields.remove(index)}>
                <Close fontSize='small' />
              </Button>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Grid>
  </div>
);
